import React, { useId } from "react"
import { observer } from "mobx-react"
import { Button, Tooltip } from "@mantine/core"
import { IconCheck, IconFileText, IconLink } from "@tabler/icons-react"
import { agentDirectionsFor } from "../../../docs/catalog"
import { prefetchAgentDirections } from "../../../docs/lazy"
import type { AgentStack } from "../../../docs/types"
import { useCopyAgentDirections, usePreloadAgentDirections } from "../../hooks/useCopyAgentDirections"
import type { CopyCtaLocation } from "../../../analytics"

type CopyAgentDirectionsButtonsProps = {
  stack: AgentStack
  framework: string
  ctaLocation: CopyCtaLocation
  size?: "sm" | "md" | "lg"
  // "filled" where copying the link is the page's main call to action, "default"
  // where it sits beside one. Copy text stays the secondary button.
  variant?: "filled" | "default"
  className?: string
  // Fetch the text once the page is idle — set on a page about this one stack.
  // Elsewhere (the homepage modal's many rows) it is fetched when a pointer or
  // focus reaches the buttons.
  preload?: boolean
}

const ICON_SIZE = { sm: 16, md: 18, lg: 20 } as const

// The one way to copy agent directions on this site, drawn wherever a stack is
// offered. "Agent directions" is a label, not a button-shaped segment. The two
// actual buttons say exactly what they put on the clipboard: the full text, or
// the absolute URL of the cover at /agent-directions/<stack>.md that fetches it.
export const CopyAgentDirectionsButtons: React.FC<CopyAgentDirectionsButtonsProps> = observer(
  ({ stack, framework, ctaLocation, size = "md", variant = "filled", className, preload = false }) => {
    const { copiedKind, copy, copyLink } = useCopyAgentDirections(framework, ctaLocation)
    usePreloadAgentDirections(stack, preload)
    const directions = agentDirectionsFor(stack)
    const copied = copiedKind(stack)
    const iconSize = ICON_SIZE[size]
    const labelId = useId()
    const groupClass = ["or-copy-directions", className].filter(Boolean).join(" ")

    const choice = (
      kind: "directions" | "link",
      label: string,
      copiedLabel: string,
      tooltip: string,
      icon: React.ReactNode,
      onClick: () => void
    ) => {
      const done = copied === kind
      return (
        <Tooltip label={done ? copiedLabel : tooltip} withArrow openDelay={200}>
          <Button
            className={`or-copy-directions-${kind}`}
            size={size}
            variant={kind === "link" ? variant : "default"}
            leftSection={done ? <IconCheck size={iconSize} /> : icon}
            onClick={onClick}
            aria-label={done ? copiedLabel : tooltip}
          >
            {done ? copiedLabel : label}
          </Button>
        </Tooltip>
      )
    }

    return (
      <div
        className={groupClass}
        role="group"
        aria-labelledby={labelId}
        onPointerEnter={() => prefetchAgentDirections(stack)}
        onPointerDown={() => prefetchAgentDirections(stack)}
        onFocus={() => prefetchAgentDirections(stack)}
      >
        <span className="or-copy-directions-label" id={labelId}>
          Agent directions
        </span>
        <Button.Group className="or-copy-directions-actions">
          {choice(
            "link",
            "Copy link",
            "Link copied",
            `Copy a link to the agent directions at ${directions.coverPath}`,
            <IconLink size={iconSize} />,
            () => void copyLink(stack)
          )}
          {choice(
            "directions",
            "Copy text",
            "Text copied",
            "Copy the full agent directions as text",
            <IconFileText size={iconSize} />,
            () => void copy(stack)
          )}
        </Button.Group>
      </div>
    )
  }
)
